import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { EmptySection } from '../../../components/EmptySection';
import { Loader } from '../../../components/Loader/Loader';
import { DownloadConfig, FilterType } from '../../../schemas/DownloadConfig.schema';
import { fetchDownloadConfigurations } from '../../../services/queries/MaggieInvestigatorQueries';
import { useAsync } from '../../../util/hook-utils';
import { AtlasSourceFilesSection } from './AtlasSourceFiles';
import { TableDownloadDialog } from './TableDownloadDialog';

// The generic exporter's dedicated Atlas config — its Source Files list is
// the single place CS investigates a delivery's data.
const ATLAS_LIVE_ROW_TABLE = 'atlasLiveRow';
// A delivery already fixes these, so its export dialog doesn't offer them as filters.
const DELIVERY_FIXED_COLUMNS = ['sourceObject', 'deliveryId', 'fundAccountantType'];

// A delivery's rows export in file order. Row sorts but isn't a filter: the server rejects unconfigured filter columns.
const DELIVERY_SORT_OPTIONS = [{ columnName: 'rowIndex', displayName: 'Row' }];
const DELIVERY_DEFAULT_SORT = { sortBy: 'rowIndex', sortOrder: 'ASC' } as const;

export interface IDeliveryScope {
  deliveryId: number;
  sourceObject: string;
}

// Not filters CS picks by hand: Export rows scopes by delivery id itself, and a tenant has one fund accountant.
const HIDDEN_ATLAS_FILTER_COLUMNS = ['deliveryId', 'fundAccountantType'];

// Source Object becomes a picker of the source objects that actually have deliveries.
function atlasDialogConfig(config: DownloadConfig, sourceObjects: string[] | null): DownloadConfig {
  return {
    ...config,
    filterableColumns: config.filterableColumns
      .filter((col) => !HIDDEN_ATLAS_FILTER_COLUMNS.includes(col.columnName))
      .map((col) =>
        col.columnName === 'sourceObject' && sourceObjects
          ? { ...col, filterType: FilterType.ENUM, enumValues: sourceObjects }
          : col
      ),
  };
}

function scopeConfigToDelivery(
  config: DownloadConfig,
  { deliveryId, sourceObject }: IDeliveryScope
): DownloadConfig {
  return {
    ...config,
    displayName: `${sourceObject} rows — delivery ${deliveryId}`,
    filterableColumns: config.filterableColumns.filter(
      (col) => !DELIVERY_FIXED_COLUMNS.includes(col.columnName)
    ),
  };
}

export function TableDataDownloadLoader() {
  const { data, loading, error } = useAsync(fetchDownloadConfigurations, {
    id: 'table-download-configs',
  });

  if (!data) {
    return (
      <Box width='100%' height='100%' m='1rem'>
        {loading ? (
          <Loader />
        ) : error ? (
          <Alert severity='error'>Failed to load export configurations</Alert>
        ) : null}
      </Box>
    );
  }

  return <TableDataDownload configs={data as DownloadConfig[]} />;
}

interface ITableDataDownloadProps {
  configs: DownloadConfig[];
}

export function TableDataDownload({ configs }: ITableDataDownloadProps) {
  const [selectedConfig, setSelectedConfig] = useState<DownloadConfig | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deliveryScope, setDeliveryScope] = useState<IDeliveryScope | null>(null);
  const [atlasSourceObjects, setAtlasSourceObjects] = useState<string[] | null>(null);
  const handleSourceObjectsLoaded = useCallback(
    (sourceObjects: string[]) => setAtlasSourceObjects(sourceObjects),
    []
  );
  const enabledConfigs = configs.filter((config) => config.enabled);

  const handleDownloadClick = () => {
    if (selectedConfig) {
      setDeliveryScope(null);
      setIsDialogOpen(true);
    }
  };

  const handleExportDeliveryRows = (scope: IDeliveryScope) => {
    setDeliveryScope(scope);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setDeliveryScope(null);
  };

  return (
    <Typography color='text.primary' component='section' width='100%' p='1rem 2rem'>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='1rem'>
        <Typography variant='h3'>Export Data</Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' mb='2rem'>
        Export data from configured tables with optional filters. Select a table below to get started.
      </Typography>

      {enabledConfigs.length === 0 ? (
        <EmptySection title='No Export Configurations' message='No tables are configured for export' />
      ) : (
        <>
          <Box sx={{ maxWidth: 600 }}>
            <Stack spacing={2}>
              <Autocomplete
                options={enabledConfigs}
                getOptionLabel={(option) => option.displayName}
                value={selectedConfig}
                onChange={(_, newValue) => setSelectedConfig(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Table'
                    placeholder='Type to search tables...'
                    helperText='Start typing to filter tables'
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant='body1'>{option.displayName}</Typography>
                      {option.description && (
                        <Typography variant='caption' color='text.secondary' display='block'>
                          {option.description}
                        </Typography>
                      )}
                      <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'monospace' }}>
                        {option.tableName}
                      </Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
              />

              {selectedConfig && selectedConfig.tableName !== ATLAS_LIVE_ROW_TABLE && (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant='subtitle2'>Table Details</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Table Name:</strong> {selectedConfig.tableName}
                    </Typography>
                    {selectedConfig.description && (
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Description:</strong> {selectedConfig.description}
                      </Typography>
                    )}
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Available Filters:</strong>{' '}
                      {selectedConfig.tableName === ATLAS_LIVE_ROW_TABLE
                        ? atlasDialogConfig(selectedConfig, atlasSourceObjects).filterableColumns.length
                        : selectedConfig.filterableColumns.length}
                    </Typography>
                    {selectedConfig.excludedColumns && selectedConfig.excludedColumns.length > 0 && (
                      <Typography variant='body2' color='text.secondary'>
                        <strong>Excluded Columns:</strong> {selectedConfig.excludedColumns.length}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              <Button
                variant='contained'
                color='primary'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={!selectedConfig}
                size='large'
              >
                Export Data
              </Button>
            </Stack>
          </Box>

          {selectedConfig?.tableName === ATLAS_LIVE_ROW_TABLE && (
            <AtlasSourceFilesSection
              onExportRows={handleExportDeliveryRows}
              onSourceObjectsLoaded={handleSourceObjectsLoaded}
            />
          )}
        </>
      )}

      {selectedConfig && isDialogOpen && (
        <TableDownloadDialog
          config={
            deliveryScope
              ? scopeConfigToDelivery(selectedConfig, deliveryScope)
              : selectedConfig.tableName === ATLAS_LIVE_ROW_TABLE
                ? atlasDialogConfig(selectedConfig, atlasSourceObjects)
                : selectedConfig
          }
          open={isDialogOpen}
          onClose={handleCloseDialog}
          initialFilters={deliveryScope ? { deliveryId: deliveryScope.deliveryId } : undefined}
          extraSortOptions={deliveryScope ? DELIVERY_SORT_OPTIONS : undefined}
          defaultSort={deliveryScope ? DELIVERY_DEFAULT_SORT : undefined}
        />
      )}
    </Typography>
  );
}
